import React, { useEffect, useMemo, useState } from 'react';
import { OfficeUsageContentWrapper } from "./OfficeUsageStructure";
import styled from "styled-components";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeDropDownList } from '@shared/ui/organisms/OfficeDropDownList';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { Moment } from "moment";
import { BodyRegular, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import TeamMultiSelect from '@shared/ui/organisms/team-drop-down-list/TeamMultiSelect';
import { TeamEntity } from '@shared/models/team.models';
import { Tag } from '@shared/models/tags.models';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';
import { selectAllTeams } from "@shared/store/config.duck";
import { selectAllTags } from '@shared/store/userTags.duck';
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import Colours from '@shared/ui/atoms/Colours';
import { DateRangePicker } from '@shared/ui/molecules/DateRangePicker';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import moment from "moment";

export function OfficeUsageHeader(props: Props) {
  const {setDate, setToDate, setRootOfficeSelected, setOfficeFloorSelected, fromDate, toDate, loading, selectedRootOffice} = props;
  const {t} = useTranslation();
  const [showCustomRange, setShowCustomRange] = useState(false);
  const offices = useSelector(selectAllOfficeEntities);
  const rootOffices = useMemo(() => offices.filter((o: OfficeEntity) => !o.parentId), [offices]);
  const officeFloors = useMemo(() => offices.filter((o: OfficeEntity) => o.parentId === selectedRootOffice?.id), [offices, selectedRootOffice?.id]);
  const teams = useSelector(selectAllTeams);
  const workGroups = useSelector(selectAllTags);
  const departments = useSelector(selectAllDepartments);
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    if (currentUser && !selectedRootOffice && rootOffices.length > 0) {
      setRootOfficeSelected(rootOffices[0])
    }
  }, [currentUser, rootOffices, selectedRootOffice, setRootOfficeSelected]);

  const onRootOfficeSelected = (office: OfficeEntity) => {
    setRootOfficeSelected(office);
  }

  const officeFloorSelected = (office: OfficeEntity) => {
    setOfficeFloorSelected(office);
  }

  const onDateChange = (date: Moment) => {
    setDate(date);
  }

  const onGroupSelected = (teams: TeamEntity[], workGroups: Tag[], offices: OfficeEntity[], departments: Department[], showLineReports?: boolean, showFavourites?: boolean, selectedMisc?: string[]) => {
    const team = teams.length > 0 ? teams[0] : undefined;
    const workGroup = workGroups.length > 0 ? workGroups[0] : undefined;
    const department = departments.length > 0 ? departments[0] : undefined;
    props.onGroupSelected(team, workGroup, department);
  }

  const onDateRangeSelected = (from?: Moment, to?: Moment) => {
    if (from) {
      setDate(from);
    }
    if (to) {
      setToDate(to);
    }
  }

  const toggleCustomRange = (show: boolean) => {
    setShowCustomRange(show);
    if (!show) {
      setDate((fromDate ?? moment()).startOf('month'));
      setToDate(undefined)
    }
  }

  return (
    <Wrapper>
      <Header>
        <OfficeUsageContentWrapper>
          <GoBackButton url={"/manage/reporting"} />
          <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.office-usage-report')}</HeadlineLarge>
          <ControlsRow>
            <DateControlsContainer>
              <DateFlicker dateScale={DateScale.Month}
                           updateGlobalDate={false}
                           onChange={onDateChange}
                           date={fromDate}
                           disabled={loading} />
              <BodyVerySmall onClick={() => toggleCustomRange(!showCustomRange)}
                             className={'datePicker__customRange'}
                             colour={Colours.blue}>{t(showCustomRange ? 'reports-page.office-usage.exit-custom' : 'reports-page.office-usage.custom')}</BodyVerySmall>
            </DateControlsContainer>
            <DropDownContainer>
              <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.office-usage.choose-office')}</BodyRegular>
              <OfficeDropDownList offices={rootOffices}
                                  preSelectedOffice={selectedRootOffice?.id}
                                  onSelect={onRootOfficeSelected}
                                  disabled={loading} />
            </DropDownContainer>
            <DropDownContainer>
              <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.office-usage.choose-group')}</BodyRegular>
              <TeamMultiSelect onSelect={onGroupSelected}
                               isClearable={true}
                               teams={teams}
                               workGroups={workGroups}
                               departments={departments}
                               selectedTeams={props?.selectedTeam ? [props.selectedTeam] : []}
                               selectedWorkGroups={props?.selectedWorkGroup ? [props.selectedWorkGroup] : []}
                               selectedDepartments={props?.selectedDepartment ? [props.selectedDepartment] : []}
                               singleSelect={true}
                               disabled={loading}  />
            </DropDownContainer>
            <DropDownContainer>
              <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.office-usage.choose-floor')}</BodyRegular>
              <OfficeDropDownList offices={officeFloors}
                                  isClearable={true}
                                  preSelectedOffice={selectedRootOffice?.id}
                                  onSelect={officeFloorSelected}
                                  disabled={loading} />
            </DropDownContainer>
          </ControlsRow>
          {showCustomRange && <DateRangeWrapper>
            <DateRangePicker disabled={loading}
                             onDatesSelected={onDateRangeSelected}
                             fromDate={fromDate}
                             inlineText={true}
                             toDate={toDate} />
          </DateRangeWrapper>}
        </OfficeUsageContentWrapper>
      </Header>
    </Wrapper>
  )
}

interface Props {
  onGroupSelected: (team?: TeamEntity, workGroup?: Tag, department?: Department) => any;
  setDate: (fromDate: Moment) => any;
  setToDate: (toDate?: Moment) => any;
  setRootOfficeSelected: (office: OfficeEntity) => any;
  setOfficeFloorSelected: (office: OfficeEntity) => any;
  selectedRootOffice?: OfficeEntity;
  fromDate?: Moment;
  toDate?: Moment;
  loading: boolean;
  selectedTeam?: TeamEntity;
  selectedWorkGroup?: Tag;
  selectedDepartment?: Department;
}

const Header = styled.div`
  width: 100%;
  background-color: white;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
  min-height: 50px;
  .contentWrapper--header {
    flex-direction: column;
  }
  padding: 6px 16px;
  @media (${tablet}) {
    padding: 6px 0;
    .contentWrapper--header {
      flex-direction: row;
    }
  }
`

const Wrapper = styled.div`
  background-color: white;
  padding-bottom: 24px;
  padding-top: 32px;
`

const DropDownContainer = styled.div`
  max-width: 100%;
  margin-bottom: 16px;
  @media (${tablet}) {
    margin-bottom: 0;
    width: 25%;
    max-width: 320px;
    margin-right: 32px;
    &:last-child {
      margin-right: 0;
    }
  }
`

const DateControlsContainer = styled.div`
  margin-right: auto;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  width: 100%;
  margin-bottom: 16px;
  .weekFlicker__control {
    padding-left: 0;
  }
  .datePicker__customRange {
    text-decoration: underline;
    cursor: pointer;
    text-align: center;
  }
  @media (${tablet}) {
    width: 20%;
    margin-bottom: 0;
    .datePicker__customRange {
      text-decoration: underline;
      cursor: pointer;
      text-align: left;
    }
  }
`

const ControlsRow = styled.div`
  display: flex;
  margin-bottom: 12px;
  flex-direction: column;
  @media (${tablet}) {
    flex-direction: row;
  }
`

const DateRangeWrapper = styled.div`
  max-width: 400px;
  margin-top: 24px;
  p {
    color: ${Colours.darkGrey};
  }
`
